import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import {
  getPrincipalName,
  MATTER_SELECTION_PAGE_SIZE,
  MATTER_SELECTION_QUERY,
  toMatterOption,
} from '../dashboard/addTransaction/matterSelection';
import { useTask } from '../../context/TaskContext';
import { useFetchMatters } from '../../hooks/useFetchMatters';
import { useOwnerSelectFilter } from '../../hooks/useOwnerSelectFilter';
import { useFetchTransactionRestrictions } from '../../hooks/useFetchTransactionRestrictions';
import { useHandleTransactionRestrictions } from '../../hooks/useHandleTransactionRestrictions';
import { IMatter } from '../../interface/matter';
import { ITask } from '../../interface/tasks';

/**
 * The props `SearchableSelect` needs to pick a task's matter, and the restriction dialogs that
 * picking one can raise. The matter is applied only after the same checks the picker page makes.
 */
export const useTaskMatterSelect = () => {
  const { task, updateTask } = useTask();
  const [searchValue, setSearchValue] = useState('');
  const { isMyOwn, filters } = useOwnerSelectFilter();
  const [selectedMatter, setSelectedMatter] = useState<IMatter | undefined>(undefined);
  const {
    data: matters,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchMatters({
    ...MATTER_SELECTION_QUERY,
    sortBy: ['openingDate:DESC'],
    pageSize: MATTER_SELECTION_PAGE_SIZE,
    search: searchValue,
    myOwn: isMyOwn,
  });
  const { data: matterTransactionRestrictions, isLoading: isCheckingRestrictions } =
    useFetchTransactionRestrictions({
      matterId: selectedMatter?.id ?? '',
      enabled: selectedMatter?.id !== undefined,
    });

  const applyMatter = (matter: IMatter) => {
    updateTask({ ...task, matter, matterId: matter.id } as ITask);
    setSelectedMatter(undefined);
  };
  const {
    customerIdentificationErrorDialog,
    setCustomerIdentificationErrorDialog,
    serviceRestrictionErrorDialog,
    setServiceRestrictionErrorDialog,
  } = useHandleTransactionRestrictions({
    selectedMatter,
    matterTransactionRestrictions,
    onSelect: applyMatter,
  });

  const options: SelectOption[] = matters.map((matter: IMatter) =>
    toMatterOption(matter, getPrincipalName(matter)),
  );
  const value: SelectOption | null = task?.matter?.id
    ? toMatterOption(task.matter, getPrincipalName(task.matter))
    : null;

  const onChange = (option: SelectOption) => {
    if (option.value === task?.matter?.id) return;
    const matter = matters.find(({ id }: IMatter) => id === option.value);
    if (!matter) return;
    setSelectedMatter(matter);
  };

  return {
    select: {
      options,
      value,
      onChange,
      onSearch: setSearchValue,
      onLoadMore: fetchNextPage,
      hasMore: Boolean(hasNextPage),
      loading: isLoading,
      loadingMore: isFetchingNextPage,
      filters,
    },
    dialogs: {
      customerIdentificationErrorDialog,
      setCustomerIdentificationErrorDialog,
      serviceRestrictionErrorDialog,
      setServiceRestrictionErrorDialog,
      onDismiss: () => setSelectedMatter(undefined),
    },
    isCheckingRestrictions,
  };
};
